import * as React from 'react';

import {useManageSaveState} from 'shells/saved-setting-toast/use-manage-save-state';

import type {
    ChatbotResponseTimingEnum,
    ChatbotToneEnum,
    RecommendationsEnum,
} from 'nutshell-graphql-types';
import type {
    GetBehaviorSettings_chatSettings as BehaviorSettings,
    ChatSettingsInput,
    ChatSettingsOfflineFormTypeEnum as OfflineFormTypeEnum,
} from 'nutshell-graphql-types';

import {useUpdateChatSettings} from '../../graphql/hooks';
import {useGenerateRecommendations} from '../../graphql/hooks/use-generate-recommendations';

import {SAVE_DELAY} from '../consts';
import type {SaveStateKey} from '../consts';

import {BehaviorSection} from './behavior-section';
import {GuidanceSection} from './guidance-section';
import {HandoffRulesSection} from './handoff-rules-section';

type Props = {
    chatSettings: BehaviorSettings;
    refetchSettings: () => Promise<any>;
    responseTiming: ChatbotResponseTimingEnum;
    setResponseTiming: (value: ChatbotResponseTimingEnum) => void;
    hasNoContent: boolean;
    tone: ChatbotToneEnum;
    setTone: (value: ChatbotToneEnum) => void;
    formType: OfflineFormTypeEnum;
    setFormType: (type: OfflineFormTypeEnum) => void;
    message: string;
    setMessage: (message: string) => void;
    setPreviewedSectionId: (sectionId?: string | null | undefined) => void;
};

export function ChatBehaviorOptionsContent(props: Props) {
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [savingToastKey, setSavingToastKey] = React.useState<any>(null);
    const [isGeneratingRecommendations, setIsGeneratingRecommendations] = React.useState<{
        SUGGESTIONS: boolean;
        TOPICS_TO_AVOID: boolean;
    }>({
        SUGGESTIONS: false,
        TOPICS_TO_AVOID: false,
    });

    const {updateChatSettings} = useUpdateChatSettings();
    const {toastState} = useManageSaveState(isSaving, SAVE_DELAY);
    const {generateRecommendations} = useGenerateRecommendations();

    const isDisabled = props.responseTiming === 'NEVER';

    const handleSave = (updates: ChatSettingsInput, toastKey: SaveStateKey) => {
        setIsSaving(true);
        setSavingToastKey(toastKey);
        updateChatSettings({
            chatSettings: updates,
        }).then(() => {
            props.refetchSettings();
            setIsSaving(false);
            setTimeout(() => setSavingToastKey(null), 3000);
        });
    };

    const handleGenerateRecommendations = (type: RecommendationsEnum) => {
        if (isDisabled) {
            return;
        }
        setIsGeneratingRecommendations((prev) => ({...prev, [type]: true}));
        generateRecommendations({type})
            .then(() => props.refetchSettings())
            .finally(() => setIsGeneratingRecommendations((prev) => ({...prev, [type]: false})));
    };

    return (
        <div className='flex flex-dir-col gap-32 full-height pr-8'>
            <BehaviorSection
                chatSettings={props.chatSettings}
                responseTiming={props.responseTiming}
                setResponseTiming={props.setResponseTiming}
                hasNoContent={props.hasNoContent}
                tone={props.tone}
                setTone={props.setTone}
                savingToastKey={savingToastKey}
                toastState={toastState}
                handleSave={handleSave}
                isDisabled={isDisabled}
            />
            <GuidanceSection
                chatSettings={props.chatSettings}
                savingToastKey={savingToastKey}
                toastState={toastState}
                handleSave={handleSave}
                handleGenerateRecommendations={handleGenerateRecommendations}
                isGeneratingRecommendations={isGeneratingRecommendations}
                isDisabled={isDisabled}
            />
            <HandoffRulesSection
                chatSettings={props.chatSettings}
                savingToastKey={savingToastKey}
                toastState={toastState}
                handleSave={handleSave}
                formType={props.formType}
                setFormType={props.setFormType}
                message={props.message}
                setMessage={props.setMessage}
                setPreviewedSectionId={props.setPreviewedSectionId}
            />
        </div>
    );
}
